import React from "react";
import { CheckCircle2Icon, MinusIcon, PlusIcon } from "lucide-react";
import { firm } from "../../data/site";
import { useCaseReviewForm } from "../../hooks/useCaseReviewForm";
import { actionClass } from "../../utils/actionStyles";
import { FormField } from "./FormField";
import { SiteLink } from "../SiteLink";
type CaseReviewFormProps = {
  showReviewNotes: boolean;
};
export function CaseReviewForm({
  showReviewNotes
}: CaseReviewFormProps) {
  const {
    values,
    errors,
    errorList,
    previewSubmitted,
    showOptional,
    setShowOptional,
    summaryRef,
    statusRef,
    handleChange,
    handleSubmit,
    reset
  } = useCaseReviewForm();
  if (previewSubmitted) {
    return <div ref={statusRef} tabIndex={-1} role="status" className="rounded-lg border-2 border-ink bg-white p-7 md:p-9" data-id="element-59" data-mp="18qf664kcwyr">
        <CheckCircle2Icon aria-hidden="true" className="h-8 w-8 text-ink" data-id="element-60" data-mp="a96kzm8s4g6s" />
        <h3 className="mt-4 text-h3 font-bold text-ink" data-id="element-61" data-mp="1qoi79wx36yu6">Preview only — nothing was sent</h3>
        <p className="mt-3 max-w-measure text-slate" data-id="element-62" data-mp="1mn6e5hf07vh8">
          This design prototype does not transmit form information. In production, the firm’s approved confirmation
          message appears here.
        </p>
        <button type="button" onClick={reset} className={actionClass("outline", "mt-6")} data-id="element-63" data-mp="1cpigojq0k9vd">
          Reset the preview form
        </button>
      </div>;
  }
  return <form noValidate onSubmit={handleSubmit} aria-labelledby="case-review-form-title" className="rounded-lg border border-line bg-white p-4 md:p-5" data-id="element-64" data-mp="25do115d5nwuo">
      <h2 id="case-review-form-title" className="text-h3 font-extrabold text-ink" data-id="element-65" data-mp="20kl6atwn6hcw">
        Free Case Review
      </h2>
      <p className="mt-1 text-meta text-slate" data-id="element-66" data-mp="2t2yxwbrdx73j">Tell us how to reach you.</p>

      {errorList.length > 0 && <div ref={summaryRef} tabIndex={-1} role="alert" aria-labelledby="form-error-title" className="mt-6 rounded-md border-2 border-crimson bg-white p-5" data-id="element-67" data-mp="2oc8qyu3b8w6q">
          <h4 id="form-error-title" className="text-label font-bold text-crimson" data-id="element-68" data-mp="qfa5byb6gkvp">
            Please fix {errorList.length === 1 ? "this item" : `these ${errorList.length} items`}:
          </h4>
          <ul className="mt-2 list-disc space-y-1 pl-6 text-meta" data-id="element-69" data-mp="hmymoymb21qa">
            {errorList.map(item => <li key={item.name} data-id="element-70" data-mp="2po4l6f5pxll">
                <a href={`#cr-${item.name}`} className="font-semibold text-ink underline underline-offset-4" data-id="element-71" data-mp="7q2bgl50v1i1">
                  {item.message}
                </a>
              </li>)}
          </ul>
        </div>}

      <div className="form-fields mt-3 grid grid-cols-2 gap-3" data-id="element-72" data-mp="3uvtfretdf0nr">
        <FormField id="cr-firstName" label="First name" autoComplete="given-name" value={values.firstName} onChange={handleChange("firstName")} error={errors.firstName} data-id="element-73" data-mp="1o4ljkl3pu164" />
        <FormField id="cr-lastName" label="Last name" autoComplete="family-name" value={values.lastName} onChange={handleChange("lastName")} error={errors.lastName} data-id="element-74" data-mp="237vaz1cx8z7g" />
        <FormField id="cr-phone" label="Phone number" type="tel" inputMode="tel" autoComplete="tel" value={values.phone} onChange={handleChange("phone")} error={errors.phone} data-id="element-75" data-mp="278ysrtej55bb" />
        <FormField id="cr-email" label="Email" type="email" autoComplete="email" value={values.email} onChange={handleChange("email")} error={errors.email} data-id="element-76" data-mp="2h0urn6ofwzgl" />
      </div>

      <div className="mt-3 border-t border-line pt-1" data-id="element-77" data-mp="10ljqa2jmge5e">
        <button type="button" aria-expanded={showOptional} aria-controls="cr-optional" onClick={() => setShowOptional(!showOptional)} className="inline-flex min-h-[48px] items-center gap-3 text-label font-bold text-ink hover:text-crimson" data-id="element-78" data-mp="wiwtroc0a51v">
          <span className="flex h-7 w-7 items-center justify-center rounded border-2 border-ink" data-id="element-79" data-mp="170qvemf7za91">
            {showOptional ? <MinusIcon aria-hidden="true" className="h-4 w-4" data-id="element-80" data-mp="1bysf99kgzdlk" /> : <PlusIcon aria-hidden="true" className="h-4 w-4" data-id="element-81" data-mp="fadrid0u0jnn" />}
          </span>
          Add details (optional)
        </button>
        {showOptional && <div id="cr-optional" className="mt-4 grid gap-6" data-id="element-82" data-mp="oa95eplrusrd">
            <div className="sm:max-w-[14rem]" data-id="element-83" data-mp="o8y3kvbvzv2w">
              <FormField id="cr-zip" label="ZIP code" optional inputMode="numeric" autoComplete="postal-code" value={values.zip} onChange={handleChange("zip")} error={errors.zip} data-id="element-84" data-mp="t1iw7tj2424u" />
            </div>
            <FormField id="cr-details" label="Crash details" optional multiline hint="A few sentences is enough." value={values.details} onChange={handleChange("details")} error={errors.details} data-id="element-85" data-mp="3p5s298yenn2p" />
          </div>}
      </div>

      <div className="mt-1 rounded-md bg-ivory px-3 py-2 text-meta text-slate" data-id="element-86" data-mp="368qtre5n74u0">
        <p data-id="element-87" data-mp="31gpc1cppglwl">
          Read our{" "}
          <SiteLink path="/privacy-policy/" className="font-semibold text-ink underline underline-offset-4" data-id="element-88" data-mp="31fo0760v7vdt">Privacy Policy</SiteLink>
          . Prefer to talk?{" "}
          <a href={firm.phoneHref} className="whitespace-nowrap font-semibold text-ink underline underline-offset-4" data-id="element-89" data-mp="2rjkpvcvv0qfs">
            Call {firm.phoneDisplay}
          </a>
          .
        </p>
      </div>

      <button type="submit" className={actionClass("call", "mt-3 w-full !whitespace-normal")} data-id="element-90" data-mp="2ijfr1r4qf0d8">
        Request a case review
      </button>
      <p className="mt-2 text-meta text-slate" data-id="element-91" data-mp="28wvadlyv1u08">All four contact fields are required.</p>
    </form>;
}